import {
  ConfigurationDomain,
  TableName,
} from '@/domain/configuration/blocs/stores/column-preferences-keys';
import {
  configurationStorage,
  STORAGE_KEYS,
} from '@/domain/configuration/blocs/utils/storage';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { createMMKV } from 'react-native-mmkv';
import { PricingGroupBy } from '@/domain/pricing/types';

export interface ColumnsPreferences {
  order: string[];
  visibility: Record<string, boolean>;
}

export interface EntityPreferences {
  columns?: ColumnsPreferences;
  groupBy?: PricingGroupBy;
}

type DomainPreferences = Partial<Record<TableName, EntityPreferences>>;

export const EMPTY_COLUMNS_PREFERENCES: ColumnsPreferences = {
  order: [],
  visibility: {},
};

interface ColumnPreferencesState {
  preferences: Record<ConfigurationDomain, DomainPreferences>;
}

interface ColumnPreferencesActions {
  reorderColumns: (
    domain: ConfigurationDomain,
    table: TableName,
    order: string[],
  ) => void;
  resetToDefaults: (domain: ConfigurationDomain, table: TableName) => void;
  setGroupBy: (
    domain: ConfigurationDomain,
    table: TableName,
    groupBy: PricingGroupBy,
  ) => void;
  toggleColumnVisibility: (
    domain: ConfigurationDomain,
    table: TableName,
    columnId: string,
    currentVisibility: boolean,
  ) => void;
}

type ColumnPreferencesStore = ColumnPreferencesState & ColumnPreferencesActions;

const LEGACY_SALES_PORTAL_STORAGE_ID = 'sales-portal-storage';
const LEGACY_COLUMN_PREFERENCES_KEY = 'column_preferences';
const LEGACY_ORDER_ENTRY_COLUMN_PREFERENCES_KEY =
  'order_entry_column_preferences';
const LEGACY_MIGRATION_DONE_KEY = 'column_preferences_legacy_migrated';

// Gated cleanup of legacy sales-portal MMKV keys. Keep the gate flag if this is removed.
const migrateLegacyStorage = (): void => {
  if (configurationStorage.getBoolean(LEGACY_MIGRATION_DONE_KEY)) {
    return;
  }
  const legacy = createMMKV({ id: LEGACY_SALES_PORTAL_STORAGE_ID });
  legacy.remove(LEGACY_COLUMN_PREFERENCES_KEY);
  legacy.remove(LEGACY_ORDER_ENTRY_COLUMN_PREFERENCES_KEY);
  configurationStorage.set(LEGACY_MIGRATION_DONE_KEY, true);
};

migrateLegacyStorage();

const buildEmptyPreferences = (): Record<
  ConfigurationDomain,
  DomainPreferences
> => ({
  [ConfigurationDomain.Ordering]: {},
  [ConfigurationDomain.Pricing]: {},
});

const updateEntity = (
  state: ColumnPreferencesState,
  domain: ConfigurationDomain,
  table: TableName,
  updater: (current: EntityPreferences) => EntityPreferences,
): ColumnPreferencesState => ({
  preferences: {
    ...state.preferences,
    [domain]: {
      ...state.preferences[domain],
      [table]: updater(state.preferences[domain][table] ?? {}),
    },
  },
});

const updateColumns = (
  state: ColumnPreferencesState,
  domain: ConfigurationDomain,
  table: TableName,
  updater: (current: ColumnsPreferences) => ColumnsPreferences,
): ColumnPreferencesState =>
  updateEntity(state, domain, table, (entity) => ({
    ...entity,
    columns: updater(entity.columns ?? EMPTY_COLUMNS_PREFERENCES),
  }));

export const useColumnPreferencesStore = create<ColumnPreferencesStore>()(
  persist(
    (set) => ({
      preferences: buildEmptyPreferences(),

      reorderColumns: (domain, table, order) =>
        set((state) =>
          updateColumns(state, domain, table, (current) => ({
            ...current,
            order,
          })),
        ),

      // Resets the columns only; the table's grouping is a separate choice.
      resetToDefaults: (domain, table) =>
        set((state) => {
          const entity = state.preferences[domain][table];
          if (!entity) {
            return state;
          }
          const next = { ...state.preferences[domain] };
          if (entity.groupBy === undefined) {
            delete next[table];
          } else {
            next[table] = { groupBy: entity.groupBy };
          }
          return {
            preferences: { ...state.preferences, [domain]: next },
          };
        }),

      setGroupBy: (domain, table, groupBy) =>
        set((state) =>
          updateEntity(state, domain, table, (entity) => ({
            ...entity,
            groupBy,
          })),
        ),

      toggleColumnVisibility: (domain, table, columnId, currentVisibility) =>
        set((state) =>
          updateColumns(state, domain, table, (current) => ({
            ...current,
            visibility: {
              ...current.visibility,
              [columnId]: !currentVisibility,
            },
          })),
        ),
    }),
    {
      name: STORAGE_KEYS.COLUMN_PREFERENCES,
      storage: createJSONStorage(() => ({
        getItem: (key) => configurationStorage.getString(key) ?? null,
        removeItem: (key) => configurationStorage.remove(key),
        setItem: (key, value) => configurationStorage.set(key, value),
      })),
      version: 1,
    },
  ),
);
